import { AbasDoAluno, useMostraAbasDoAluno } from '@/features/aluno/AbasDoAluno';
import { ChatBubbleLeftEllipsisIcon } from '@heroicons/react/24/outline';
import { ITEM_DO_RODAPE_DA_GAVETA } from '@/components/layout/itemDaGaveta';
import { abrirGavetaDaArea } from '@/components/layout/gavetaDaArea';
import { PuxarParaAtualizar, telaQuePuxa } from '@/components/layout/PuxarParaAtualizar';
import { useState, Fragment, useEffect, useRef } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react';
// Os seis ícones que sobravam aqui eram da `ADMIN_ITEMS`. Saíram com ela — o
// compilador os apontou como não usados, que é a prova de que a lista morreu.
import {
    AcademicCapIcon,
    Bars3Icon,
    HomeIcon,
    XMarkIcon,
} from '@heroicons/react/24/outline';
import SairDaConta from '@/components/layout/SairDaConta';
import { SimboloDoPortal } from '@/components/marca/SimboloDoPortal';
import { CabecaDaRota } from '@/seo/CabecaDaRota';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { AppTopBar } from '@/components/app/AppTopBar';
import { getTitleFromPath } from '@/utils/routeTitles';
import { useFullscreenOverlay } from '@/features/teacher/layout/FullscreenOverlayContext';
import { FEATURES } from '@/config/features';
import { SinoDeNotificacoes } from '@/features/notificacoes/SinoDeNotificacoes';
import { NotificacoesNoAplicativo } from '@/features/notificacoes/NoAplicativo';
import { BotaoDeRelato } from '@/features/relatos/components/RelatarAlgo';
import { podeRelatarNesteMomento } from '@/features/relatos/podeRelatar';

/*
 * TEACHER_ITEMS foi REMOVIDA daqui, e não corrigida.
 *
 * Era uma lista paralela do menu do professor, escrita quando havia dois
 * relatórios: trazia "Relatórios (Por Simulado)" e "Relatórios (Por Aluno)" e
 * nunca acompanhou os oito que vieram depois. No celular ela aparecia AO LADO da
 * gaveta do `TeacherLayout` — dois menus, um com dez relatórios e outro com dois.
 *
 * Corrigi-la manteria viva justamente o que causou a divergência, e ela
 * divergiria de novo no próximo relatório. Deixá-la inalcançável seria pior:
 * código que ninguém vê e que a próxima pessoa tentaria consertar.
 *
 * ADMIN_ITEMS e getStudentItems FICAM, mas por razões diferentes:
 *
 *   `/student/*` não tem layout próprio nenhum — este menu é o único que existe
 *   para o aluno, em qualquer largura.
 *
 *   `/admin/*` TEM gaveta própria (`AdminLayout`, botão "Menu" em `lg:hidden`),
 *   e por isso entra em `temGavetaPropria` junto com `/teacher`. `ADMIN_ITEMS`
 *   sobrevive porque continua servindo o administrador FORA da área dele — em
 *   `/`, `/sobre` —, onde não há barra nenhuma.
 *
 *   (Fica a assimetria: o professor recebe UM item de retorno fora da área dele
 *   e o administrador recebe a lista inteira. A lista do admin é alcançável, e
 *   portanto não é código morto; mas é uma lista paralela ao `AdminSidebar` e
 *   pode envelhecer do mesmo jeito. Decisão em aberto.)
 */

/*
 * `ADMIN_ITEMS` foi REMOVIDA — era a pendência registrada no achado 93.
 *
 * Ela repetia à mão a lista do `AdminSidebar`, e o achado previu que divergiria,
 * como a `TEACHER_ITEMS` divergira em oito relatórios antes de ser apagada. Não
 * chegou a divergir: agora esta gaveta MONTA o `AdminSidebar`, e não há segunda
 * lista para envelhecer.
 *
 * O aluno é o único papel que segue com lista aqui, e por um motivo que não é
 * descuido: não existe `StudentSidebar`. Quando existir, este ramo vira o quarto
 * componente montado e a última lista desta tela some.
 */

const STUDENT_ITEMS_BASE = [
    { path: '/student', label: 'Meus simulados', icon: HomeIcon },
    { path: '/student/quiz', label: 'Quizzes Disponíveis', icon: AcademicCapIcon },
];
const getStudentItems = () =>
    FEATURES.studentQuiz ? STUDENT_ITEMS_BASE : STUDENT_ITEMS_BASE.filter((i) => i.path !== '/student/quiz');

const ROOT_ROUTES = ['/teacher', '/admin', '/student'];

import { useQuery } from '@tanstack/react-query';
import TeacherSidebar from '@/features/teacher/components/TeacherSidebar';
import AdminSidebar from '@/features/admin/components/AdminSidebar';
import GestaoSidebar from '@/features/gestao/components/GestaoSidebar';
import { getMinhaEscola } from '@/features/gestao/api/gestaoApi';
import { RenovarCredenciaisDasSalas } from '@/features/aplicativo/RenovarCredenciaisDasSalas';
import { PreparoAutomatico } from '@/features/aplicativo/PreparoAutomatico';
import { AvisoDeSalaEsquecida, NoticiaDasSalas } from '@/features/aplicativo/NoticiaDasSalas';

/**
 * A lista de links do ALUNO — o único papel sem componente de barra próprio.
 *
 * Está aqui, e não solta no meio do JSX, para que o dia em que existir um
 * `StudentSidebar` a troca seja de uma linha, como foi para os outros três.
 */
const ListaDeItens = ({
    itens,
    aoNavegar,
}: {
    itens: { path: string; label: string; icon: React.ComponentType<{ className?: string }> }[];
    aoNavegar: () => void;
}) => {
    const location = useLocation();
    return (
        <nav className="h-full overflow-y-auto px-2 pb-4 flex flex-col">
            {itens.map((item) => {
                const Icon = item.icon;
                const active = location.pathname.startsWith(item.path);
                return (
                    <Link
                        key={item.path}
                        to={item.path}
                        onClick={aoNavegar}
                        className={`block w-full flex items-center px-2 py-3 text-sm min-h-[44px] rounded-md ${
                            active ? 'bg-marca-50 text-marca-600' : 'text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                        <Icon className="h-5 w-5 mr-3 flex-shrink-0" />
                        {item.label}
                    </Link>
                );
            })}
        </nav>
    );
};

/** Os links do rodapé com 44 px de altura no celular (alvo de toque); no desktop, como sempre. */
const LINK_DO_RODAPE = 'inline-flex min-h-[44px] items-center sm:min-h-0 text-gray-600 hover:text-marca-600';

const MainLayout = () => {
    const { user, logout, isAuthenticated } = useAuth();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    /*
     * SAIR DA PROVA SÓ COM CONFIRMAÇÃO (27/10/2026, revisão do pesquisador). Na prova REAL do aluno (`/student/simulation/…`;
     * a prévia do professor não conta), toda saída pelo cabeçalho — o logo, o "voltar" e o menu do aplicativo, o botão Voltar
     * do Android — pede antes "Sua prova continua aberta…". O "Sair" da conta, "Meus simulados" e "Olá, nome" não aparecem
     * durante a prova. Antes eles estavam lá e levavam para fora sem aviso (o tempo continuava correndo).
     */
    const [saidaDaProva, setSaidaDaProva] = useState<null | (() => void)>(null);
    const provaDoAluno = useLocation().pathname.startsWith('/student/simulation/');
    const provaDoAlunoRef = useRef(provaDoAluno);
    provaDoAlunoRef.current = provaDoAluno;
    const comConfirmacao = (sair: () => void) => () => { if (provaDoAlunoRef.current) setSaidaDaProva(() => sair); else sair(); };
    const location = useLocation();
    const navigate = useNavigate();
    const isNative = Capacitor.isNativePlatform();
    const { fullscreenOverlay, telaCheia } = useFullscreenOverlay();

    const closeMenu = () => setMobileMenuOpen(false);

    const handleTopBarBack = () => {
        const path = location.pathname.replace(/\/$/, '') || '/';
        const isRoot = ROOT_ROUTES.includes(path);
        if (isRoot) {
            if (user?.role === 'TEACHER') navigate('/teacher/simulations');
            else if (user?.role === 'ADMIN') navigate('/admin');
            else if (user?.role === 'STUDENT') navigate('/student');
            else navigate('/');
        } else {
            navigate(-1);
        }
    };

    const pathnameRef = useRef(location.pathname);
    pathnameRef.current = location.pathname;

    // Android: botão físico Voltar — nas raízes não navega; nas demais volta na pilha
    useEffect(() => {
        if (!isNative) return;
        const handler = () => {
            const path = (pathnameRef.current || '/').replace(/\/$/, '') || '/';
            const isRoot = ROOT_ROUTES.includes(path);
            if (isRoot) return;
            if (provaDoAlunoRef.current) setSaidaDaProva(() => () => navigate(-1)); else navigate(-1);
        };
        let handle: { remove: () => Promise<void> } | null = null;
        App.addListener('backButton', handler).then((h) => {
            handle = h;
        });
        return () => {
            handle?.remove();
        };
    }, [isNative, navigate]);

    /**
     * A escola de quem é da gestão — a MESMA chave de query do `GestaoProvider`.
     *
     * O `GestaoSidebar` recebe `podeAdministrar` para não oferecer item que
     * devolve 409, e `convitesPendentes` para o contador. Fora de `/gestao` o
     * provider não está montado, e os padrões do componente (`true`, `0`)
     * mostrariam ao coordenador porta que ele não pode abrir — o achado 83 de
     * novo, pela porta dos fundos.
     *
     * Ler pela mesma chave é o que evita isso sem duplicar nada: quando a pessoa
     * entra em `/gestao` o provider aproveita este cache, e vice-versa. Uma
     * requisição por sessão, e só para quem é da gestão.
     */
    const { data: escolaDaGestao } = useQuery({
        queryKey: ['gestao', 'minha-escola'],
        queryFn: getMinhaEscola,
        retry: false,
        enabled: user?.role === 'SCHOOL_STAFF',
    });

    /**
     * O CONTEÚDO da gaveta, por papel — quatro ramos enumerados, sem `else`.
     *
     * Antes daqui saía uma LISTA de itens, montada à mão. Três dessas listas
     * já apodreceram: a `TEACHER_ITEMS` divergiu em oito relatórios (achado 95),
     * a `ADMIN_ITEMS` foi registrada como a próxima a divergir (achado 93), e a
     * da gestão só não nasceu porque o `GestaoLayout` ganhou gaveta antes.
     *
     * Agora esta gaveta monta O MESMO COMPONENTE que a área usa. As três
     * superfícies — desktop, gaveta da área, gaveta daqui — ficam iguais por
     * CONSTRUÇÃO, não por sincronia. Não há o que manter alinhado.
     *
     * ENUMERAR, NÃO EXCLUIR. Os quatro papéis estão nomeados e o desconhecido cai
     * em `null`, que aqui significa "sem gaveta" — a mesma disciplina dos achados
     * 83 e 84: um `else` daria a alguém futuro o menu de outra pessoa.
     *
     * O papel desconhecido não abre gaveta vazia porque o GATILHO também depende
     * disto: ver `temGaveta` abaixo.
     */
    const conteudoDaGaveta = () => {
        if (!user) return null;
        if (user.role === 'TEACHER') return <TeacherSidebar variant="drawer" onNavigate={closeMenu} />;
        if (user.role === 'ADMIN') return <AdminSidebar variant="drawer" onNavigate={closeMenu} />;
        if (user.role === 'SCHOOL_STAFF') {
            return (
                <GestaoSidebar
                    podeAdministrar={escolaDaGestao?.podeAdministrar ?? false}
                    convitesPendentes={escolaDaGestao?.contagens?.convitesPendentes ?? 0}
                    professoresSemTurma={escolaDaGestao?.contagens?.professoresSemTurma ?? 0}
                    onNavigate={closeMenu}
                    variant="drawer"
                />
            );
        }
        if (user.role === 'STUDENT') return <ListaDeItens itens={getStudentItems()} aoNavegar={closeMenu} />;
        return null;
    };

    /**
     * Os três papéis que montam BARRA — e não lista.
     *
     * Governa duas coisas de uma vez, porque são a mesma:
     *
     * 1. A barra rola POR DENTRO (`h-full` na raiz, `overflow-y-auto` no
     *    `<nav>` — achado 96), então o invólucro aqui só prende a altura,
     *    com `overflow-hidden`. A lista do aluno não rola por dentro, e ali
     *    quem rola é o invólucro.
     * 2. A barra já TRAZ o próprio rodapé de Sair. Repeti-lo no painel daria
     *    dois botões na mesma gaveta.
     *
     * O aluno é o único sem barra — e por isso o único que ainda precisa do
     * rodapé daqui. Não é `else`: é o quarto ramo, nomeado.
     */
    const papelTemBarra = user?.role === 'TEACHER' || user?.role === 'ADMIN' || user?.role === 'SCHOOL_STAFF';
    const naProva = location.pathname.startsWith('/student/simulation/') || location.pathname.startsWith('/previa/');

    // Só há gaveta se há conteúdo. Papel desconhecido não ganha gatilho que abre
    // caixa vazia — foi o achado 83.
    const temGaveta = !!user && ['TEACHER', 'ADMIN', 'SCHOOL_STAFF', 'STUDENT'].includes(user.role);
    // O aluno no celular navega pelas abas de baixo (23/10/2026): sem o ☰ da gaveta de um item só.
    const alunoComAbas = useMostraAbasDoAluno();

    /**
     * Áreas que trazem a PRÓPRIA gaveta abaixo de `lg`, e onde este gatilho
     * seria o segundo menu na tela.
     *
     * `/admin` está aqui porque `AdminLayout` também tem botão "Menu" e Dialog
     * em `lg:hidden` — medido: dois gatilhos em 390px, exatamente como acontecia
     * na área do professor.
     *
     * `/gestao` entrou depois: o `GestaoLayout` ganhou gaveta pelo mesmo molde,
     * em vez de uma `SCHOOL_STAFF_ITEMS` aqui — que seria a terceira lista
     * paralela, logo depois de a do professor sair por ter divergido.
     */
    const temGavetaPropria = ['/teacher', '/admin', '/gestao'].some((r) => location.pathname.startsWith(r));

    return (
        /*
          `flex flex-col` NA RAIZ, sem o qual o `mt-auto` do rodapé é inerte.
 
          A raiz já era `min-h-screen` e o rodapé já pedia `mt-auto` — mas
          `mt-auto` só empurra dentro de um contêiner FLEX, e este era bloco.
          Com pouco conteúdo o rodapé ficava logo depois do `<main>`, no meio da
          página, e o fundo do `min-h-screen` seguia abaixo dele. Medido em
          1440×900: 287px de branco sob o rodapé em /gestao/simulados, 233px em
          /gestao/turmas.
 
          É primo do achado 90 e não o mesmo: lá uma altura de viewport era
          declarada num contêiner que não começava no topo da viewport; aqui a
          declaração está certa e o ANCESTRAL não oferece o contexto que ela
          precisa. A família é a mesma — propriedade que depende de um ancestral,
          e o ancestral não cumpre — e o sintoma também: espaço vazio que ninguém
          pediu.
        */
        // EM TELA CHEIA (a montagem de simulado) a raiz tem a ALTURA DA JANELA, e não "no mínimo" ela:
        // é o que faz a lista da página rolar por dentro, em vez de a página inteira. Somem o
        // cabeçalho e o rodapé — a página desenha a própria barra. E sai `main-layout-wrapper`, cujas
        // regras de aplicativo (`index.css`) fariam o `<main>` rolar por conta própria.
        // Os três trocam de CLASSE ou somem por condição, e o `<main>` fica onde está: mudar a forma
        // da árvore remontaria a página, que é quem liga o sinal.
        <div className={telaCheia ? 'h-[100dvh] flex flex-col overflow-hidden bg-fundo' : 'min-h-screen flex flex-col bg-fundo main-layout-wrapper'}>
            {/* O título, a descrição, o canonical e o robots de cada rota (`seo/CabecaDaRota.tsx`). */}
            <CabecaDaRota />
            {/* No aplicativo de computador: libera o envio das salas sem internet com credencial vencida (etapa 5). */}
            <RenovarCredenciaisDasSalas />
            {/* A sala sem internet preparada sozinha no aparelho marcado, e o envio ao abrir o app do celular (02/11/2026). */}
            <PreparoAutomatico />
            {/* A notícia das salas deste aparelho ao portal, e a fila "encerrar no portal" (03/11/2026, `encerrarSala.ts`). */}
            <NoticiaDasSalas />
            {/* A notificação do sistema no aplicativo do computador 2.0.12+ (Plano B, etapa b); no navegador não faz nada. */}
            <NotificacoesNoAplicativo />
            {/* No APLICATIVO o menu do topo segue o MESMO portão do gatilho do
                navegador: onde a área tem gaveta própria, ele se cala. Sem isto
                ele era o único menu do app — e depois que `TEACHER_ITEMS` saiu,
                passou a ter UM item. Ver achado 95. */}
            {/* Puxar para atualizar: só no app e só nas listas (`telaQuePuxa`) — nunca na prova. */}
            <PuxarParaAtualizar ligado={isNative && isAuthenticated && telaQuePuxa(location.pathname)} />
            {isNative && !fullscreenOverlay && (
                <AppTopBar
                    title={getTitleFromPath(location.pathname)}
                    showBack
                    onBack={comConfirmacao(handleTopBarBack)}
                    showMenu={isAuthenticated && temGaveta && !alunoComAbas && !provaDoAluno}
                    onMenuClick={() => (temGavetaPropria ? abrirGavetaDaArea() : setMobileMenuOpen(true))}
                    direita={alunoComAbas ? <BotaoDeRelato className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-md text-gray-600 hover:bg-gray-100" rotulo="Relatar algo"><ChatBubbleLeftEllipsisIcon className="h-6 w-6" aria-hidden /><span className="sr-only">Relatar algo</span></BotaoDeRelato> : <SinoDeNotificacoes />}
                />
            )}
            {!isNative && !telaCheia && (
                <header className="bg-white shadow-sm sticky top-0 z-50">
                    {/* Durante a prova (do aluno, ou a pré-visualização), a barra encolhe: o enunciado da questão 1 tem de
                        caber na primeira tela do celular e de telas baixas (25/10/2026). */}
                    <div className={`container mx-auto px-4 ${naProva ? 'py-1' : 'py-4'}`}>
                        <div className="flex justify-between items-center">
                            {/* Uma linha só: com o símbolo, o sino e o menu, a 390 px o nome quebrava em dois (25/10/2026). */}
                            <Link to="/" onClick={(e) => { if (provaDoAluno) { e.preventDefault(); setSaidaDaProva(() => () => navigate('/')); } }} className="inline-flex min-w-0 items-center gap-2 whitespace-nowrap font-titulo text-xl font-bold text-marca md:text-2xl">
                                <SimboloDoPortal className="h-6 w-6 flex-shrink-0 md:h-7 md:w-7" />
                                Portal Simulados
                            </Link>

                            {/*
                              O gatilho some onde a área TEM GAVETA PRÓPRIA.
                              ────────────────────────────────────────────────
                              `/teacher/*` é servida pelo `TeacherLayout`, que
                              abaixo de `lg` mostra o próprio botão "Menu" com o
                              `TeacherSidebar` em gaveta. As duas condições se
                              sobrepunham — `md:hidden` daqui está contido no
                              `lg:hidden` de lá —, e abaixo de 768px o professor
                              via DOIS menus com listas diferentes.

                              Por ROTA e não por papel: o professor também visita
                              `/`, `/sobre` e `/privacidade`, que são servidas por
                              este layout e não têm barra nenhuma. Esconder por
                              papel o deixaria sem menu ali também.

                              `/admin` e `/gestao` NÃO entram: as duas têm `aside`
                              de desktop e nenhuma tem gaveta. Esconder o gatilho
                              lá tiraria o único menu que sobra no celular.
                            */}
                            {/* O sino no celular fica no topo, à vista, em toda área — inclusive onde o
                                gatilho do menu some (área com gaveta própria). */}
                            <div className="flex items-center md:hidden ml-auto mr-1">
                                {/* O aluno tem as abas embaixo (o sino é a aba "Notificações"): aqui fica o "Relatar algo". */}
                                {alunoComAbas
                                    ? <BotaoDeRelato className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-md text-gray-600 hover:bg-gray-100" rotulo="Relatar algo"><ChatBubbleLeftEllipsisIcon className="h-6 w-6" aria-hidden /><span className="sr-only">Relatar algo</span></BotaoDeRelato>
                                    : <SinoDeNotificacoes />}
                            </div>
                            {/* Nas áreas com gaveta própria (professor, gestão, administração), o ☰ também fica AQUI, na barra, até
                                `lg` — igual ao app (23/10/2026, revisão do pesquisador). Antes cada área tinha um botão "Menu" solto
                                abaixo da barra. Quem abre a gaveta é a área (`gavetaDaArea.ts`). */}
                            {isAuthenticated && temGavetaPropria && (
                                <button
                                    type="button"
                                    className="flex lg:hidden p-2 rounded-md text-gray-600 hover:bg-gray-100 min-h-[44px] min-w-[44px] items-center justify-center"
                                    aria-label="Abrir menu"
                                    data-testid="menu-da-barra"
                                    onClick={abrirGavetaDaArea}
                                >
                                    <Bars3Icon className="h-6 w-6" />
                                </button>
                            )}
                            {isAuthenticated && temGaveta && !temGavetaPropria && !alunoComAbas && !provaDoAluno && (
                                <button
                                    type="button"
                                    className="flex md:hidden p-2 rounded-md text-gray-600 hover:bg-gray-100 min-h-[44px] min-w-[44px] items-center justify-center"
                                    aria-label="Abrir menu"
                                    onClick={() => setMobileMenuOpen(true)}
                                >
                                    <Bars3Icon className="h-6 w-6" />
                                </button>
                            )}

                            {/* Desktop: nav */}
                            <nav className="hidden md:flex items-center space-x-6">
                                {isAuthenticated && provaDoAluno ? (
                                    // Na prova: uma saída só, e com confirmação.
                                    <button type="button" onClick={comConfirmacao(() => navigate('/student'))} className="text-gray-600 hover:text-gray-900" data-testid="voltar-aos-simulados-da-prova">
                                        Voltar aos simulados
                                    </button>
                                ) : isAuthenticated ? (
                                    <>
                                        {/* O nome é o CAMINHO para a conta no desktop.
                                            `SairDaConta` — que traz o item "Minha conta" —
                                            só monta em gaveta, e a gaveta é `md:hidden`:
                                            sem este link, acima de 768px não haveria como
                                            chegar em `/conta`, e portanto na exclusão. */}
                                        <Link to="/conta" className="text-gray-600 hover:text-marca-600">
                                            Olá, {user?.name}
                                        </Link>
                                        {/* O canal de relato mora AQUI e na gaveta: os dois
                                            lugares que existem em toda tela, para os quatro
                                            papéis. Não é botão flutuante — flutuante alcança
                                            de qualquer lugar e atrapalha em todos. */}
                                        {podeRelatarNesteMomento(location.pathname) && (
                                            <BotaoDeRelato
                                                className="flex items-center gap-1.5 text-gray-600 hover:text-marca-600"
                                                rotulo="Relatar algo"
                                            />
                                        )}
                                        {user?.role === 'ADMIN' && (
                                            <Link to="/admin" className="text-marca-600 hover:text-marca-800">
                                                Área Administrativa
                                            </Link>
                                        )}
                                        {user?.role === 'TEACHER' && (
                                            <Link to="/teacher" className="text-marca-600 hover:text-marca-800">
                                                Área do Professor
                                            </Link>
                                        )}
                                        {user?.role === 'STUDENT' && (
                                            <>
                                                <Link to="/student" className="text-marca-600 hover:text-marca-800">
                                                    Meus simulados
                                                </Link>
                                                {FEATURES.studentQuiz && (
                                                    <Link to="/student/quiz" className="text-marca-600 hover:text-marca-800">
                                                        Quizzes Disponíveis
                                                    </Link>
                                                )}
                                            </>
                                        )}
                                        {/* Um lugar só para avisos: o sino (25/10/2026). A bandeja de PDFs saiu — o PDF pedido é um
                                            aviso do sino desde o início ("Gerando…" → "pronto", com "Baixar"). */}
                                        <SinoDeNotificacoes />
                                        <button
                                            onClick={logout}
                                            // Cinza: sair não é ação perigosa; o vermelho fica para o que apaga (24/10/2026, design D).
                                            className="text-gray-600 hover:text-gray-900"
                                        >
                                            Sair
                                        </button>
                                    </>
                                ) : location.pathname === '/login' ? null : (
                                    // Na própria tela de entrar, sem o "Entrar" repetido no canto (24/10/2026).
                                    <Link to="/login" className="text-marca-600 hover:text-marca-800">
                                        Entrar
                                    </Link>
                                )}
                            </nav>
                        </div>
                    </div>
                </header>
            )}

            {/* Mobile drawer */}
            <Transition.Root show={mobileMenuOpen} as={Fragment}>
                {/* `md:hidden` no NAVEGADOR, e em lugar nenhum no APLICATIVO.
                    A classe espelhava o gatilho do cabeçalho, que é `flex md:hidden`
                    — mas o gatilho do `AppTopBar` NÃO tem ponto de corte: no app ele
                    aparece por `temGaveta && !temGavetaPropria`, em qualquer largura.
                    O iPhone permite paisagem (`Info.plist`), e ali a viewport passa
                    de 768px: o botão Menu aparecia e abria uma gaveta com
                    `display:none`. Quem cai nisso é o ALUNO, o único papel sem barra
                    própria — e o que ele perde é o Sair e o caminho para `/conta`. */}
                <Dialog as="div" className={`relative z-50 ${isNative ? '' : 'md:hidden'}`} onClose={closeMenu}>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
                    </Transition.Child>
                    <div className="fixed inset-0 flex">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="-translate-x-full"
                            enterTo="translate-x-0"
                            leave="ease-in duration-200"
                            leaveFrom="translate-x-0"
                            leaveTo="-translate-x-full"
                        >
                            <Dialog.Panel
                                className="relative flex w-full max-w-[260px] flex-col bg-white shadow-xl"
                                style={{
                                    paddingTop: 'env(safe-area-inset-top)',
                                    paddingBottom: 'env(safe-area-inset-bottom)',
                                }}
                            >
                                <div className="flex items-center justify-between px-4 pt-4 pb-2 flex-shrink-0">
                                    <span className="text-lg font-semibold text-gray-800">Menu</span>
                                    <button
                                        type="button"
                                        className="p-2 rounded-md text-gray-500 hover:bg-gray-100 min-h-[44px] min-w-[44px] flex items-center justify-center"
                                        aria-label="Fechar menu"
                                        onClick={closeMenu}
                                    >
                                        <XMarkIcon className="h-6 w-6" />
                                    </button>
                                </div>
                                {/* O invólucro que PRENDE a altura — sem ele a barra montada
                                    cresce até o conteúdo e o excedente é aparado em silêncio.
                                    É o achado 96, e é o mesmo invólucro do `TeacherLayout`.
                                    `overflow-hidden` onde a barra rola por dentro;
                                    `overflow-y-auto` onde quem rola é este contêiner. */}
                                <div className={`flex-1 min-h-0 ${papelTemBarra ? 'overflow-hidden' : 'overflow-y-auto'}`}>
                                    {conteudoDaGaveta()}
                                </div>
                                {/*
                                  A BARRA DE UM PAPEL JÁ TRAZ O PRÓPRIO RODAPÉ (24/10/2026, pedido do pesquisador). Fora da área do papel
                                  (`/notificacoes`, `/conta`…) a gaveta é ESTA, e ela monta a barra do papel — que já termina em
                                  Notificações, Relatar algo, Minha conta, Trocar de conta e Sair. Somar aqui o sino e o "Relatar algo"
                                  os mostrava DUAS vezes, depois do Sair, e os blocos fixos espremiam a navegação até cortá-la. O rodapé
                                  daqui é só de quem não tem barra (o aluno, quando abre esta gaveta).
                                */}
                                {/* O Sair mora no rodapé das BARRAS, e elas já o trazem para
                                    professor, administrador e gestão. Sobra o aluno, que não
                                    tem barra — e é para ele que este rodapé existe. */}
                                {/* Relatar fica para os QUATRO papéis, como as notificações
                                    de PDF acima: é do layout, não da barra de nenhum papel.
                                    Por isso não está no `!papelTemBarra` do Sair. */}
                                {!papelTemBarra && (
                                <div className="flex-shrink-0 px-2">
                                    <SinoDeNotificacoes variante="item" aoNavegar={closeMenu} />
                                </div>
                                )}
                                {isAuthenticated && !papelTemBarra && podeRelatarNesteMomento(location.pathname) && (
                                    <div className="flex-shrink-0 px-2 pb-1">
                                        <BotaoDeRelato
                                            className={ITEM_DO_RODAPE_DA_GAVETA}
                                            rotulo="Relatar algo"
                                        />
                                    </div>
                                )}
                                {isAuthenticated && !papelTemBarra && <SairDaConta aoSair={closeMenu} />}
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </Dialog>
            </Transition.Root>

            {/* A página inicial (24/10/2026) tem faixas de ponta a ponta e centra o próprio conteúdo: sem o contêiner aqui. */}
            <main className={telaCheia ? 'flex-1 min-h-0 w-full flex flex-col' : location.pathname === '/' ? 'flex-1 w-full' : `container mx-auto px-4 ${naProva ? 'py-2' : alunoComAbas ? 'py-4 md:py-8' : 'py-8'} flex-1 w-full`}>
                {/* "Você tem uma sala de … que não foi encerrada" — só no aplicativo, com a liberação do servidor (03/11/2026). */}
                {!telaCheia && !naProva && <AvisoDeSalaEsquecida />}
                <Outlet />
            </main>
            {/* O aluno no celular: as três abas embaixo, no lugar da gaveta (`AbasDoAluno`); fora da prova. */}
            <AbasDoAluno />
            {saidaDaProva && (
                <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/40 p-4 sm:items-center" role="dialog" aria-modal="true" aria-labelledby="titulo-saida-da-prova" data-testid="confirmar-saida-da-prova">
                    <div className="w-full max-w-sm rounded-lg bg-white p-5 shadow-xl">
                        <h2 id="titulo-saida-da-prova" className="text-base font-semibold text-tinta">Sua prova continua aberta</h2>
                        <p className="mt-2 text-sm text-gray-700">O que você já respondeu fica guardado, mas o tempo continua correndo. Para entregar, volte à prova e use “Entregar”.</p>
                        <div className="mt-4 flex flex-col gap-2 sm:flex-row-reverse">
                            <button type="button" autoFocus onClick={() => setSaidaDaProva(null)} className="inline-flex min-h-[44px] items-center justify-center rounded-md bg-marca-700 px-4 text-sm font-medium text-white hover:bg-marca-800" data-testid="continuar-na-prova">Continuar na prova</button>
                            <button type="button" onClick={() => { const sair = saidaDaProva; setSaidaDaProva(null); sair(); }} className="inline-flex min-h-[44px] items-center justify-center rounded-md border border-gray-300 px-4 text-sm font-medium text-gray-700 hover:bg-gray-50" data-testid="sair-mesmo-assim">Sair mesmo assim</button>
                        </div>
                    </div>
                </div>
            )}

            <footer className={telaCheia ? 'hidden' : 'bg-white border-t mt-auto'}>
                <div className="container mx-auto px-4 py-6">
                    <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                        {/* O ano vem do relógio, e não do teclado: escrito à mão ele envelhece em
                            silêncio, e "© 2024" numa captura de 2026 é a primeira coisa que
                            um revisor de loja lê. */}
                        <p className="text-gray-600 text-center sm:text-left">© {new Date().getFullYear()} Portal Simulados. Todos os direitos reservados.</p>
                        <div className="flex flex-wrap justify-center gap-x-4">
                            {/* "Como usar" no rodapé porque é onde se procura este
                                tipo de link quando não se está lendo a landing. */}
                            <Link to="/como-usar" className={LINK_DO_RODAPE}>Como usar</Link>
                            <Link to="/sobre" className={LINK_DO_RODAPE}>Sobre</Link>
                            <Link to="/privacidade" className={LINK_DO_RODAPE}>Privacidade</Link>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default MainLayout;
